// src/components/BillingTab.tsx
//
// Session 36 / Consolidated invoicing Chunk B — the billing surface UI.
//
// Renders the "Billing" tab on ContactDetail and CompanyDetail. Lists this
// customer's completed unbilled visits in a date range, lets the operator
// multiselect, shows a running estimated total + invoice-level discount,
// and provides the "Combine into invoice" CTA.
//
// In Chunk B the CTA is a STUB — it toasts intent + console.logs the
// selected job ids and inputs, but performs NO database writes. The real
// consolidation (INSERT invoice + UPDATE field_jobs.invoice_id with race-
// safe predicates) lands in Chunk C behind a separate strategist halt gate.
//
// Eligibility is STRICTLY series-agnostic per the locked Model A decision:
// the WHERE clause filters on contact + status + invoice_id + date range
// only. series_id is selected solely so a small ↻ marker can render on
// recurring visits — it does NOT affect which visits appear, never.
// ============================================================================

import React, { useState, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { format, startOfMonth } from 'date-fns'
import { toast } from 'sonner'
import { Repeat, Receipt, AlertTriangle } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { snapshotInvoiceCost } from '@/lib/invoiceCostSnapshot'
import { formatCurrency, formatDate } from '@/lib/utils'
import DiscountRow from '@/components/payments/DiscountRow'
import { computeTotals, type DiscountType } from '@/lib/totals'
import {
  projectFieldJobLineItemsToInvoiceJsonb,
  buildConsolidatedInvoiceInsert,
  fetchContactEmail,
  type FieldJobLineItem,
} from '@/lib/jobInvoiceConvert'

interface Props {
  /**
   * Contact IDs whose visits should appear in this billing surface.
   * - ContactDetail passes a single-element array [contact.id]
   * - CompanyDetail passes the full set of contact ids belonging to the company
   * Pass [] while parent is still loading; the tab renders an empty state.
   */
  contactIds: string[]
}

const dateInputStyle: React.CSSProperties = {
  padding: '6px 10px',
  background: 'var(--field-bg)',
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  fontSize: 12,
  color: 'var(--ink-primary)',
  outline: 'none',
  colorScheme: 'dark',
}

export default function BillingTab({ contactIds }: Props) {
  const [rangeStart, setRangeStart] = useState(() => format(startOfMonth(new Date()), 'yyyy-MM-dd'))
  const [rangeEnd, setRangeEnd]     = useState(() => format(new Date(), 'yyyy-MM-dd'))
  const [selected, setSelected]     = useState<Set<string>>(new Set())
  const [discountType, setDiscountType]   = useState<DiscountType>('none')
  const [discountValue, setDiscountValue] = useState(0)

  // Stable key — sort prevents the key from changing when the parent's
  // contacts query returns ids in a different order across refetches.
  const contactKey = useMemo(() => [...contactIds].sort().join(','), [contactIds])

  const { data: visits = [], isLoading } = useQuery({
    queryKey: ['billing-unbilled-visits', contactKey, rangeStart, rangeEnd],
    queryFn: async () => {
      if (contactIds.length === 0) return []
      // Eligibility filter — contact + status + invoice_id + date range.
      // No series_id condition: Model A is series-agnostic by design.
      // contact_id is selected for the Q1 same-contact validation guard at
      // consolidation time (CompanyDetail's billing tab spans multiple
      // contacts; consolidation requires all selected visits to share one).
      // contact_name / company_name are selected for the contact snapshot
      // (Q4) on the new invoice's denormalized fields.
      const { data, error } = await supabase
        .from('field_jobs')
        .select('id, job_number, title, scheduled_date, subtotal, discount_type, discount_value, discount_amount, tax_amount, total, series_id, contact_id, contact_name, company_name')
        .in('contact_id', contactIds)
        .eq('status', 'complete')
        .is('invoice_id', null)
        .gte('scheduled_date', rangeStart)
        .lte('scheduled_date', rangeEnd)
        .order('scheduled_date', { ascending: true })
      if (error) throw error
      return data ?? []
    },
    enabled: contactIds.length > 0,
  })

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }
  const allSelected  = visits.length > 0 && selected.size === visits.length
  const someSelected = selected.size > 0 && selected.size < visits.length
  const toggleAll = () => {
    if (allSelected) setSelected(new Set())
    else setSelected(new Set(visits.map(v => v.id)))
  }

  // Preview math only — explicitly labeled as estimate. Chunk C does the
  // authoritative recompute via computeTotals() against the merged line items
  // from each selected job's field_job_line_items, not against the per-job
  // `total` aggregate used here.
  const { selectedTotal, discountAmount, estimateTotal } = useMemo(() => {
    const sum = visits.filter(v => selected.has(v.id))
                      .reduce((acc, v) => acc + (Number(v.total) || 0), 0)
    const dv = Number(discountValue) || 0
    const da =
      discountType === 'percentage' ? sum * (dv / 100) :
      discountType === 'fixed'      ? Math.min(dv, sum) :
      0
    return {
      selectedTotal:  Math.round(sum * 100) / 100,
      discountAmount: Math.round(da * 100) / 100,
      estimateTotal:  Math.round((sum - da) * 100) / 100,
    }
  }, [visits, selected, discountType, discountValue])

  const { workspace } = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)
  // Orphan-invoice state for the partial-failure recovery path. Populated
  // when step 4's UPDATE throws entirely after step 3's INSERT succeeded —
  // i.e. an invoice row exists but no field_jobs.invoice_id pointers were
  // set. The "Retry linking" button re-runs ONLY the guarded UPDATE for
  // the same invoice id + selectedIds; the UPDATE is idempotent (its
  // .is('invoice_id', null) predicate makes re-running safe).
  const [orphan, setOrphan] = useState<{ invoiceId: string; jobIds: string[] } | null>(null)

  // The load-bearing UPDATE. Lifted to component scope so handleCombine
  // AND handleRetryLink share one source of truth for the WHERE clause.
  // Two explicit chained predicates, individually visible:
  //   .in('id', jobIds)       — scope to exactly the selected jobs
  //   .is('invoice_id', null) — RACE GUARD: drops any job that was already
  //                             invoiced (per-job path, another tab) between
  //                             BillingTab's load and this click. Single
  //                             source of double-invoicing defense.
  // RLS adds the workspace_id predicate at the DB layer (FOR ALL
  // workspace_isolation USING + WITH CHECK from baseline_rls.sql:88-91).
  const linkJobsToInvoice = async (invoiceId: string, jobIds: string[]) => {
    const { data: updatedRows, error: updErr } = await supabase
      .from('field_jobs')
      .update({ invoice_id: invoiceId })
      .in('id', jobIds)
      .is('invoice_id', null)
      .select('id')
    if (updErr) return { kind: 'error' as const, error: updErr }
    const linked = (updatedRows ?? []).map(r => r.id)
    const linkedSet = new Set(linked)
    const skipped = jobIds.filter(id => !linkedSet.has(id))
    return { kind: 'ok' as const, linked, skipped }
  }

  const invalidateAfterWrite = () => {
    queryClient.invalidateQueries({ queryKey: ['billing-unbilled-visits'] })
    queryClient.invalidateQueries({ queryKey: ['invoices'] })
    queryClient.invalidateQueries({ queryKey: ['contact-invoices'] })
    queryClient.invalidateQueries({ queryKey: ['company-invoices'] })
    queryClient.invalidateQueries({ queryKey: ['field-jobs-cal'] })
    queryClient.invalidateQueries({ queryKey: ['field-jobs-ready-for-invoice'] })
    queryClient.invalidateQueries({ queryKey: ['activity-invoices'] })
  }

  const handleCombine = async () => {
    // (6) Falsy guards — before any write. Belt-and-suspenders against an
    // empty / degenerate selection slipping past the disabled-button state.
    if (selected.size === 0) { toast.error('Select at least one visit'); return }
    const selectedRows = visits.filter(v => selected.has(v.id))
    const selectedIds = selectedRows.map(r => r.id)
    if (selectedIds.length === 0 || selectedIds.some(id => !id)) {
      toast.error('Invalid selection — please reselect and try again'); return
    }
    if (!workspace?.id) { toast.error('No workspace context'); return }

    // (Q1) MANDATORY same-contact validation. Financial-correctness guard —
    // a consolidated invoice billing one contact for another contact's
    // visits is silent financial corruption. The visits query already
    // selects contact_id + contact_name, so this reads from the loaded
    // rows (no extra round-trip).
    const distinctContactIds = new Set(selectedRows.map(r => r.contact_id))
    if (distinctContactIds.size > 1) {
      const names = Array.from(new Set(selectedRows.map(r => r.contact_name).filter(Boolean)))
      toast.error(
        `Selected visits span multiple contacts (${names.join(', ')}). Narrow the selection to one contact, or use that contact's own Billing tab.`,
        { duration: 10_000 },
      )
      return
    }

    setSubmitting(true)
    try {
      // (1) Gather. IN over all selected ids; one round-trip.
      const { data: lineRows, error: lineErr } = await supabase
        .from('field_job_line_items')
        .select('*')
        .in('field_job_id', selectedIds)
        .order('field_job_id', { ascending: true })
        .order('sort_index', { ascending: true })
      if (lineErr) throw lineErr
      const mergedLineItems = projectFieldJobLineItemsToInvoiceJsonb((lineRows ?? []) as FieldJobLineItem[])

      // (2) Authoritative totals via the shared helper. tax_rate=0 per Q3 —
      // operator sets tax on InvoiceDetail. Discount = invoice-level only;
      // per-job discounts are NOT applied (Chunk B's row notes warned of
      // this).
      const totals = computeTotals({
        lineItems: mergedLineItems,
        discountType, discountValue, taxRate: 0,
        // Arc B Slice 1: a consolidated invoice has no single source minimum
        // (it spans multiple jobs) — no floor at consolidation. The operator can
        // set the invoice's minimum_charge on InvoiceDetail after create (CP2).
        minimumCharge: null,
      })

      // invoice_number assigned by the assign_invoice_number BEFORE-INSERT
      // trigger (Phase A, 20260615000000). buildConsolidatedInvoiceInsert
      // omits the key; the trigger atomically claims the next number.

      // (3) INSERT invoice FIRST. If this fails, NO field_jobs touched —
      // nothing is half-done. The shape comes from the shared builder.
      const firstRow = selectedRows[0]
      // field_jobs.contact_id is in scope but the row doesn't carry the
      // contact's email — fetch it so the consolidated invoice can be
      // Sent / receipt-emailed (Q1 already ensures every selected row
      // shares the same contact_id, so one lookup covers all visits).
      const contactEmail = await fetchContactEmail(firstRow.contact_id)
      const insertRow = buildConsolidatedInvoiceInsert({
        workspaceId: workspace.id,
        firstSelectedRow: {
          contact_id:    firstRow.contact_id,
          contact_name:  firstRow.contact_name ?? '',
          contact_email: contactEmail,
          company_name:  firstRow.company_name ?? null,
        },
        mergedLineItems,
        discountType, discountValue,
        totals,
        issueDate: format(new Date(), 'yyyy-MM-dd'),
      })
      const { data: invoice, error: insErr } = await supabase
        .from('invoices')
        .insert(insertRow)
        .select('id')
        .single()
      if (insErr || !invoice) {
        setSubmitting(false)
        toast.error(`Failed to create invoice: ${insErr?.message ?? 'unknown'}`)
        return
      }

      // (4) THE LOAD-BEARING UPDATE — see linkJobsToInvoice above for the
      // exact WHERE clause and predicate-by-predicate rationale.
      const linkResult = await linkJobsToInvoice(invoice.id, selectedIds)
      setSubmitting(false)

      if (linkResult.kind === 'error') {
        // Partial failure: invoice exists but no jobs linked (network drop,
        // RLS denial, etc.). Stay on the Billing tab, surface the retry
        // affordance. The orphan invoice remains voidable from InvoiceDetail
        // if the operator gives up.
        setOrphan({ invoiceId: invoice.id, jobIds: selectedIds })
        toast.error(`Invoice created but visits not linked — use "Retry linking" to attach them`)
        return
      }

      // (5) Verify updated_count === selected. If less, a race occurred —
      // some selected jobs got invoiced (per-job path / other tab) between
      // BillingTab's load and our UPDATE, and the .is('invoice_id', null)
      // race-guard correctly excluded them. Surface the specific job numbers
      // so the operator knows which visits did NOT get added.
      if (linkResult.skipped.length > 0) {
        const skippedRows = selectedRows.filter(r => linkResult.skipped.includes(r.id))
        const skippedNumbers = skippedRows.map(r => r.job_number || r.id.slice(0, 8))
        toast.warning(
          `${linkResult.skipped.length} of ${selectedIds.length} visits were already invoiced and were not added (${skippedNumbers.join(', ')}). The new invoice covers the remaining ${linkResult.linked.length}.`,
          { duration: 10_000 },
        )
      } else {
        toast.success(`Consolidated invoice created — ${linkResult.linked.length} ${linkResult.linked.length === 1 ? 'visit' : 'visits'}`)
      }

      // Slice 4: freeze the consolidated cost basis across the ACTUALLY-linked
      // jobs (Option X — the helper sums all dimensions over linkResult.linked
      // and runs jobMarginFull once). Staff-only, non-fatal — never throws.
      await snapshotInvoiceCost(supabase, workspace.id, invoice.id, linkResult.linked)
      invalidateAfterWrite()
      setSelected(new Set())
      navigate(`/app/invoices/${invoice.id}`)
    } catch (err: any) {
      setSubmitting(false)
      toast.error(`Consolidation failed: ${err?.message ?? 'unknown'}`)
    }
  }

  const handleRetryLink = async () => {
    if (!orphan) return
    setSubmitting(true)
    const linkResult = await linkJobsToInvoice(orphan.invoiceId, orphan.jobIds)
    setSubmitting(false)

    if (linkResult.kind === 'error') {
      toast.error(`Retry failed: ${linkResult.error.message ?? 'unknown'} — try again`)
      return
    }
    if (linkResult.skipped.length > 0) {
      toast.warning(
        `${linkResult.skipped.length} of ${orphan.jobIds.length} visits couldn't be linked (already invoiced). Linked ${linkResult.linked.length}.`,
        { duration: 10_000 },
      )
    } else {
      toast.success(`Linked ${linkResult.linked.length} ${linkResult.linked.length === 1 ? 'visit' : 'visits'} to the invoice`)
    }
    invalidateAfterWrite()
    const linkedTo = orphan.invoiceId
    setOrphan(null)
    setSelected(new Set())
    navigate(`/app/invoices/${linkedTo}`)
  }

  const handleDismissOrphan = () => {
    // Operator chose not to retry. Invoice row stays (voidable from
    // InvoiceDetail). Clear local recovery state so the surface returns
    // to normal.
    setOrphan(null)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Date-range header — defaults first-of-month to today, operator-adjustable. */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 14px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Period</span>
        <input type="date" value={rangeStart} onChange={e => setRangeStart(e.target.value)} style={dateInputStyle} aria-label="Range start" />
        <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>to</span>
        <input type="date" value={rangeEnd} onChange={e => setRangeEnd(e.target.value)} style={dateInputStyle} aria-label="Range end" />
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>
          {visits.length} unbilled {visits.length === 1 ? 'visit' : 'visits'}
        </span>
      </div>

      {/* Visit list. */}
      {contactIds.length === 0 ? (
        <EmptyState text="No contacts attached yet — visits will appear here once contacts have completed work." />
      ) : isLoading ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--ink-tertiary)', fontSize: 13 }}>Loading visits…</div>
      ) : visits.length === 0 ? (
        <EmptyState text="No completed unbilled visits in this period." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, cursor: 'pointer', fontSize: 12, color: 'var(--ink-secondary)' }}>
            <input
              type="checkbox"
              checked={allSelected}
              ref={el => { if (el) el.indeterminate = someSelected }}
              onChange={toggleAll}
            />
            {allSelected ? 'Deselect all' : `Select all ${visits.length}`}
          </label>
          {visits.map(v => {
            const hasPerJobDiscount = (Number(v.discount_amount) || 0) > 0 || (Number(v.discount_value) || 0) > 0
            const isSelected = selected.has(v.id)
            return (
              <label
                key={v.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 14px',
                  background: isSelected ? 'var(--accent-dim)' : 'var(--surface)',
                  border: `1px solid ${isSelected ? 'var(--accent-border)' : 'var(--border-subtle)'}`,
                  borderRadius: 10, cursor: 'pointer',
                }}>
                <input type="checkbox" checked={isSelected} onChange={() => toggle(v.id)} />
                <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--ink-tertiary)', minWidth: 70 }}>{v.job_number || '—'}</span>
                <span style={{ fontSize: 12, color: 'var(--ink-tertiary)', minWidth: 88 }}>{v.scheduled_date ? formatDate(v.scheduled_date) : '—'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {v.title || 'Untitled visit'}
                    {v.series_id && (
                      <span title="Part of a recurring series" style={{ display: 'inline-flex', flexShrink: 0 }}>
                        <Repeat style={{ width: 11, height: 11, color: 'var(--ink-secondary)' }} />
                      </span>
                    )}
                  </p>
                  {hasPerJobDiscount && (
                    <p style={{ fontSize: 11, color: 'var(--warning)', margin: '3px 0 0', fontStyle: 'italic' }}>
                      Per-visit discount won't carry to a consolidated invoice
                    </p>
                  )}
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)' }}>{formatCurrency(Number(v.total) || 0)}</span>
              </label>
            )
          })}
        </div>
      )}

      {/* Partial-failure recovery banner. Appears when an invoice was
          successfully INSERTed but the load-bearing UPDATE failed entirely
          (network drop, etc.). Retrying re-runs ONLY the guarded UPDATE
          for the same invoice id + selected job ids — idempotent because
          .is('invoice_id', null) makes re-running safe. The orphan
          invoice can also be voided from InvoiceDetail if the operator
          dismisses instead. */}
      {orphan && (
        <div style={{
          padding: '12px 14px',
          background: 'color-mix(in srgb, var(--warning) var(--status-tint-fill), transparent)',
          border: '1px solid color-mix(in srgb, var(--warning) var(--status-tint-edge), transparent)',
          borderRadius: 10,
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <AlertTriangle style={{ width: 16, height: 16, color: 'var(--warning)', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: 'var(--warning)' }}>
              Invoice created but visits not linked
            </p>
            <p style={{ margin: '3px 0 0', fontSize: 11, color: 'var(--ink-secondary)' }}>
              The invoice exists but {orphan.jobIds.length} {orphan.jobIds.length === 1 ? 'visit was' : 'visits were'} not attached. Retry to link them, or dismiss and void the orphan invoice from its detail page.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={handleDismissOrphan} disabled={submitting} className="btn-ghost" style={{ fontSize: 12, padding: '7px 12px' }}>
              Dismiss
            </button>
            <button type="button" onClick={handleRetryLink} disabled={submitting} className="btn-primary" style={{ fontSize: 12, padding: '7px 14px' }}>
              {submitting ? 'Retrying…' : 'Retry linking'}
            </button>
          </div>
        </div>
      )}

      {/* Summary card. Renders only when there are visits to operate on. */}
      {visits.length > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Estimate</span>
            <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>
              Authoritative totals are recomputed at consolidation time.
            </span>
          </div>
          <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-subtle)' }}>
            <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>Selected visits</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>{selected.size} of {visits.length}</span>
          </div>
          <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-subtle)' }}>
            <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>Subtotal</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>{formatCurrency(selectedTotal)}</span>
          </div>
          <DiscountRow
            isLocked={false}
            discountType={discountType}
            discountValue={discountValue}
            discountAmount={discountAmount}
            onTypeChange={setDiscountType}
            onValueChange={setDiscountValue}
          />
          <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--hover-bg)' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)' }}>Estimated total</span>
            <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink-primary)' }}>{formatCurrency(estimateTotal)}</span>
          </div>
          <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={handleCombine}
              disabled={selected.size === 0 || submitting || !!orphan}
              className="btn-primary"
              style={{
                padding: '9px 16px', fontSize: 13, fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', gap: 6,
                opacity: selected.size === 0 || submitting || !!orphan ? 0.5 : 1,
                cursor: selected.size === 0 || submitting || !!orphan ? 'not-allowed' : 'pointer',
              }}>
              <Receipt style={{ width: 13, height: 13 }} />
              {submitting ? 'Creating…' : 'Combine into invoice'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function EmptyState({ text }: { text: string }) {
  return (
    <div style={{ padding: '40px 16px', textAlign: 'center', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10 }}>
      <Receipt style={{ width: 24, height: 24, color: 'var(--ink-tertiary)', display: 'block', margin: '0 auto 8px' }} />
      <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>{text}</p>
    </div>
  )
}
